import apiClient from "@/lib/apiClient";

export interface WorkspaceAccountSummary {
  id: string;
  name: string;
  slug: string;
  domain?: string | null;
  created_at?: string | null;
  /** THE holding pen — prospect workspaces are parked here. Read the FLAG, never
   *  a slug: the account split renames the pen to `holding-pen` and hands
   *  `knitknot` to the real company, so a slug literal inverts. */
  is_holding_pen?: boolean;
}

export interface Workspace {
  id: string;
  account_id?: string;
  name: string;
  slug?: string;
  description?: string;
  created_at: string;
  updated_at: string;
  // Owning account, derived from the workspace server-side. Single source of
  // truth for "what account owns this page".
  account?: WorkspaceAccountSummary | null;
}

export interface WorkspaceMember {
  id: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  avatar_id?: number | null;
  profile_image_url?: string | null;
}

export type AboutPage = Record<string, unknown>;

export type AccountTier = 'free' | 'starter' | 'pro' | 'enterprise';
export type StylePreset = 'minimal' | 'professional' | 'bold' | 'custom';
export type ShadowIntensity = 'none' | 'subtle' | 'medium' | 'strong';

// Advanced typography
export type LineHeight = 'compact' | 'normal' | 'relaxed';
export type HeadingLetterSpacing = 'tight' | 'normal' | 'wide';
export type HeadingFontWeight = 'medium' | 'semibold' | 'bold' | 'black';
export type LinkUnderline = 'always' | 'hover' | 'never';

export interface DarkModeOverrides {
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  background_color?: string | null;
  text_color?: string | null;
  link_color?: string | null;
}

// Style extraction types
export type ExtractionStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface ExtractedColors {
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  background_color?: string | null;
  text_color?: string | null;
  link_color?: string | null;
}

export interface ExtractionJobRequest {
  url: string;
}

export interface ExtractionJobResponse {
  job_id: string;
  status: ExtractionStatus;
}

export type ExtractionPhase = 'loading' | 'analyzing';

export interface ExtractionJobResult {
  job_id: string;
  status: ExtractionStatus;
  phase?: ExtractionPhase | null;  // Current processing phase for progress display
  error?: string | null;

  // Extracted values
  colors?: ExtractedColors | null;
  dark_colors?: ExtractedColors | null;
  dark_mode_detected: boolean;

  // Typography
  heading_font?: string | null;
  heading_font_confidence?: number | null;
  body_font?: string | null;
  body_font_confidence?: number | null;

  // Style
  style_preset?: StylePreset | null;
  style_preset_confidence?: number | null;
  border_radius?: string | null;
  shadow_intensity?: ShadowIntensity | null;

  // Advanced typography
  line_height?: LineHeight | null;
  heading_letter_spacing?: HeadingLetterSpacing | null;
  heading_font_weight?: HeadingFontWeight | null;
  link_underline?: LinkUnderline | null;
}

// Font and preset options
export interface FontOption {
  name: string;
  category: 'sans-serif' | 'serif' | 'monospace';
  weights: number[];
}

export interface StylePresetOption {
  name: string;
  shadow_intensity: string;
  border_radius: string;
  font_scale: string;
  description: string;
}

// Palette generation
export interface PaletteGenerateRequest {
  primary_color: string;
}

export interface PaletteGenerateResponse {
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  text_color: string;
  link_color: string;
}

export interface DarkPaletteGenerateRequest {
  primary_color: string;
  secondary_color?: string | null;
  accent_color?: string | null;
  background_color?: string | null;
  text_color?: string | null;
  link_color?: string | null;
}

// Brand auto-detection types
export interface LogoCandidate {
  url: string;
  source: 'og:image' | 'twitter:image' | 'apple-touch-icon' | 'svg-favicon' | 'large-favicon' | 'dom-img' | 'favicon-fallback';
  confidence: number;
  width?: number | null;
  height?: number | null;
  format?: string | null;
}

export interface ColorCandidate {
  hex: string;
  confidence: number;
  source: 'css-variable' | 'computed-style' | 'screenshot-analysis';
}

export interface DetectedColors {
  primary?: ColorCandidate | null;
  secondary?: ColorCandidate | null;
  accent?: ColorCandidate | null;
}

export interface BrandExtractionResult {
  success: boolean;
  logo?: LogoCandidate | null;
  all_logos: LogoCandidate[];
  colors?: DetectedColors | null;
  extraction_time_ms: number;
  errors: string[];
  warnings: string[];
}

export interface BrandExtractionRequest {
  domain: string;
  extract_logo?: boolean;
  extract_colors?: boolean;
  timeout_seconds?: number;
}

class WorkspaceService {
  // Basic CRUD Operations
  async createWorkspace(data: { workspace_name: string; user_id: string; account_id: string; source_workspace_id?: string; testing_tier?: AccountTier; description?: string }): Promise<Workspace> {
    return apiClient.post("/workspaces", data);
  }

  async getWorkspace(workspaceId: string): Promise<Workspace> {
    return apiClient.get(`/workspaces/${workspaceId}`);
  }

  async updateWorkspace(workspaceId: string, data: { workspace_name?: string; slug?: string }): Promise<Workspace> {
    return apiClient.put(`/workspaces/${workspaceId}`, data);
  }

  // User Management
  async addUserToWorkspace(workspaceId: string, userId: string): Promise<void> {
    return apiClient.post(`/workspaces/${workspaceId}/users`, { user_id: userId });
  }

  async removeUserFromWorkspace(workspaceId: string, userId: string): Promise<void> {
    return apiClient.delete(`/workspaces/${workspaceId}/users/${userId}`);
  }

  async getWorkspaceUsers(workspaceId: string): Promise<WorkspaceMember[]> {
    return apiClient.get(`/workspaces/${workspaceId}/users`);
  }

  async uploadWorkspaceLogo(workspaceId: string, file: File): Promise<{ logo_url: string }> {
    console.log('[WorkspaceService] uploadWorkspaceLogo called for workspace:', workspaceId, 'file:', file.name);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const result = await apiClient.uploadFile<{ logo_url: string }>(`/workspaces/${workspaceId}/style/logo`, formData);
      console.log('[WorkspaceService] uploadWorkspaceLogo result:', result);
      return result;
    } catch (error) {
      console.error('[WorkspaceService] uploadWorkspaceLogo error:', error);
      throw error;
    }
  }

  async deleteWorkspaceLogo(workspaceId: string): Promise<void> {
    return apiClient.delete(`/workspaces/${workspaceId}/style/logo`);
  }

  async uploadWorkspaceFavicon(workspaceId: string, file: File): Promise<{ favicon_url: string }> {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.uploadFile(`/workspaces/${workspaceId}/favicon`, formData);
  }

  async deleteWorkspaceFavicon(workspaceId: string): Promise<void> {
    return apiClient.delete(`/workspaces/${workspaceId}/favicon`);
  }

  async uploadWorkspaceOgImage(workspaceId: string, file: File): Promise<{ og_image_url: string }> {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.uploadFile(`/workspaces/${workspaceId}/style/og-image`, formData);
  }

  async deleteWorkspaceOgImage(workspaceId: string): Promise<void> {
    return apiClient.delete(`/workspaces/${workspaceId}/style/og-image`);
  }

  // Brand Auto-Detection
  async autoDetectBrand(
    workspaceId: string,
    request: BrandExtractionRequest
  ): Promise<BrandExtractionResult> {
    return apiClient.post(`/workspaces/${workspaceId}/style/auto-detect`, request);
  }

  // Style System - Fonts & Presets
  async getAvailableFonts(): Promise<FontOption[]> {
    return apiClient.get('/workspaces/style/fonts');
  }

  async getStylePresets(): Promise<StylePresetOption[]> {
    return apiClient.get('/workspaces/style/presets');
  }

  // Style System - Palette Generation
  async generatePalette(
    workspaceId: string,
    request: PaletteGenerateRequest
  ): Promise<PaletteGenerateResponse> {
    return apiClient.post(`/workspaces/${workspaceId}/style/generate-palette`, request);
  }

  async generateDarkPalette(
    workspaceId: string,
    request: DarkPaletteGenerateRequest
  ): Promise<DarkModeOverrides> {
    return apiClient.post(`/workspaces/${workspaceId}/style/generate-dark-palette`, request);
  }

  // Style System - AI Extraction
  async startStyleExtraction(
    workspaceId: string,
    request: ExtractionJobRequest
  ): Promise<ExtractionJobResponse> {
    return apiClient.post(`/workspaces/${workspaceId}/style/extract`, request);
  }

  async getExtractionJobResult(
    workspaceId: string,
    jobId: string
  ): Promise<ExtractionJobResult> {
    return apiClient.get(`/workspaces/${workspaceId}/style/extract/${jobId}`);
  }

  // User's workspaces (moved from /users/list_workspaces)
  async listUserWorkspaces(userId: string): Promise<Workspace[]> {
    return apiClient.get(`/users/${userId}/workspaces`);
  }

  // About Page
  async getAboutPage(workspaceId: string): Promise<AboutPage> {
    return apiClient.get(`/workspaces/${workspaceId}/about`);
  }

  async updateAboutPage(workspaceId: string, data: AboutPage): Promise<AboutPage> {
    return apiClient.post(`/workspaces/${workspaceId}/about`, data);
  }

  async uploadAboutImage(workspaceId: string, file: File): Promise<{ image_url: string }> {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.uploadFile(`/workspaces/${workspaceId}/about/image`, formData);
  }

  async deleteAboutImage(workspaceId: string): Promise<void> {
    return apiClient.delete(`/workspaces/${workspaceId}/about/image`);
  }
}

export default new WorkspaceService();

// For backward compatibility
export const workspaceService = new WorkspaceService();
